<template>
    <div>
        <loader v-if="!this.userDefinition && !this.locationDefinition && !this.user.loaded && !this.location.loaded" />
        <template v-else>
            <div class="order-configuration">
                <!-- Original Detected Request -->
                <div v-if="extractedLocationInfo" class="mb-3 mt-3 px-3">
                    <p class="mb-0 text-muted">
                        <b-icon icon="b-info-circle" class="mr-1"></b-icon>
                        Ursprünglich erkannte Anfrage: <template v-if="extractedLocationInfo.locationIds && extractedLocationInfo.locationIds.length > 0">Id's: {{
                            extractedLocationInfo.locationIds.join(', ') }}, </template>Standort:
                        "{{ extractedLocationInfo.locationQuery }}"
                    </p>
                </div>

                <template v-if="configuration.combinedDeliveryAddress">
                    <div class="configuration-title">
                        <b-row align-v="center">
                            <b-col>
                                <h2>{{ $t('components.order.configuration.addresses.title') }}</h2>
                            </b-col>
                            <b-col cols="auto" v-if="isEditable">
                                <b-button variant="outline-primary" size="sm" @click="openDeliveryAddressSelection">
                                    <b-icon icon="b-pencil-fill" /> Lieferadresse ändern
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                    <dl class="configuration-list">
                        <template>
                            <dt>{{ $t('components.order.configuration.addresses.delivery.title') }}</dt>
                            <dd>
                                <address-line :address="configuration.combinedDeliveryAddress" inline />
                            </dd>
                        </template>
                    </dl>
                </template>
                <template v-else-if="configuration.location">
                    <div class="configuration-title mb-3">
                        <b-row align-v="center">
                            <b-col>
                                <h2>{{ $t('components.order.configuration.addresses.title') }}</h2>
                            </b-col>
                            <b-col cols="auto" v-if="isEditable">
                                <b-button variant="outline-primary" size="sm" @click="openDeliveryAddressSelection">
                                    <b-icon icon="search" /> Lieferadresse auswählen...
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                    <div class="alert alert-info mb-4">
                        <p class="mb-0">
                            <b-icon icon="b-info-circle" />
                            &nbsp;{{ $t('components.order.configuration.addresses.delivery.selection.please_select') }}
                        </p>
                    </div>
                </template>

                <template v-if="configuration.user">
                    <div class="configuration-title">
                        <b-row align-v="center">
                            <b-col>
                                <h2>{{ $t('components.order.configuration.user.title') }}</h2>
                            </b-col>

                            <b-col cols="auto">
                                <b-link :to="{ name: 'Users.Detail', params: { userid: configuration.user.id } }">{{ $t('components.order.configuration.user.detaillink') }}</b-link>
                            </b-col>
                        </b-row>
                    </div>

                    <dl v-if="!userIs.loading" class="configuration-list mb-4">
                        <template v-for="property in user">
                            <template v-if="property.visible && property.control.value">
                                <dt :key="`title-${property.name}`">{{ property.control.label }}</dt>
                                <dd :key="`value-${property.name}`" class="text-wrap-word">{{ valueFormatter(userDefinition, property.name, property.control.value).value }}</dd>
                            </template>
                        </template>
                    </dl>
                </template>

                <template v-if="configuration.location">

                    <div class="configuration-title">
                        <b-row align-v="center">
                            <b-col>
                                <h2>{{ $t('components.order.configuration.location.title') }}</h2>
                            </b-col>

                            <b-col cols="auto">
                                <b-button :to="{ name: 'Locations.Detail', params: { locationid: configuration.location.id } }" variant="outline-primary" size="sm" class="ml-2" button>
                                    <b-icon icon="search" /> {{ $t('components.order.configuration.location.detaillink') }}
                                </b-button>
                                <b-button v-if="isEditable" variant="outline-primary" size="sm" class="ml-2" @click="openLocationSearch">
                                    <b-icon icon="b-pencil-fill" /> Standort ändern
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>

                    <dl v-if="!this.locationIs.loading" class="configuration-list">
                        <template v-for="property in location">
                            <template v-if="property.visible && property.control.value">
                                <dt :key="`title-${property.name}`">{{ property.control.label }}</dt>
                                <dd :key="`value-${property.name}`" class="text-wrap-word">{{ valueFormatter(locationDefinition, property.name, property.control.value).value }}</dd>
                            </template>
                        </template>
                    </dl>
                </template>
                <template v-else>
                    <div class="configuration-title mb-3">
                        <b-row align-v="center">
                            <b-col>
                                <h2>{{ $t('components.order.configuration.location.title') }}</h2>
                            </b-col>
                            <b-col cols="auto" v-if="isEditable">
                                <b-button variant="outline-primary" size="sm" @click="openLocationSearch">
                                    <b-icon icon="search" /> Standort auswählen...
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                    <div class="alert alert-info mb-4">
                        <p class="mb-0">
                            <b-icon icon="b-info-circle" />
                            &nbsp;{{ $t('components.order.configuration.location.selection.please_select') }}
                        </p>
                    </div>
                </template>
            </div>

            <!-- Location Search Modal -->
            <b-modal :id="locationSearchModal.id" v-model="locationSearchModal.show" size="lg" centered scrollable no-fade header-bg-variant="info" header-text-variant="white" title-tag="h4"
                     title-class="align-self-center" hide-footer>
                <template #modal-header-close><b-icon icon="close" scale="0.6" /></template>
                <template #modal-title>{{ $t('components.order.configuration.location.search.title') }}</template>

                <div class="modal-search-content">
                    <!-- Original Detected Request -->
                    <div v-if="extractedLocationInfo" class="alert alert-info mb-3">
                        <p class="mb-0">
                            <b-icon icon="b-info-circle" class="mr-1"></b-icon>
                            <strong>Ursprünglich erkannte Anfrage:</strong>
                            <template v-if="extractedLocationInfo.locationIds && extractedLocationInfo.locationIds.length > 0">
                                <br />Id's: {{ extractedLocationInfo.locationIds.join(', ') }}
                            </template>
                            <template v-if="extractedLocationInfo.locationQuery">
                                <br />Standort: "{{ extractedLocationInfo.locationQuery }}"
                            </template>
                        </p>
                    </div>

                    <div class="search-control mb-4">
                        <control-input v-model="locationSearchModal.searchQuery" :placeholder="$t('components.order.configuration.location.search.placeholder')" @input="searchLocations"
                                       id="locationSearchQuery" />
                    </div>

                    <div v-if="locationSearchModal.isLoading" class="text-center py-4">
                        <loader type="dots" inline />
                        <p class="text-muted mt-2 mb-0">{{ $t('components.order.configuration.location.search.searching') }}</p>
                    </div>

                    <div v-else-if="locationSearchModal.results.length === 0 && locationSearchModal.searchQuery" class="text-center py-4">
                        <b-icon icon="search" class="text-muted mb-2" scale="2" />
                        <p class="text-muted mb-0">{{ $t('components.order.configuration.location.search.noResults') }}</p>
                    </div>

                    <div v-else-if="locationSearchModal.results.length > 0" class="search-results">
                        <b-list-group flush>
                            <b-list-group-item v-for="location in locationSearchModal.results" :key="location.id"
                                               :class="{ 'selected': locationSearchModal.selectedLocation && locationSearchModal.selectedLocation.id === location.id }"
                                               @click="selectLocationFromList(location)" button>
                                <div class="d-flex justify-content-between align-items-start">
                                    <div class="flex-grow-1">
                                        <h6 class="mb-1 font-weight-semibold text-start">{{ location.name }}</h6>
                                        <div class="text-muted small text-start">
                                            {{ $t('components.order.configuration.location.search.customerId') }} {{ location.customerId }}<br />
                                            {{ $t('components.order.configuration.location.search.address') }} {{ location.address }}
                                        </div>
                                    </div>
                                    <div class="selection-indicator">
                                        <b-icon v-if="locationSearchModal.selectedLocation && locationSearchModal.selectedLocation.id === location.id" icon="check-circle-fill" variant="success"
                                                scale="1.2" />
                                    </div>
                                </div>
                            </b-list-group-item>
                        </b-list-group>
                    </div>

                    <div v-else class="text-center py-4">
                        <b-icon icon="search" class="text-muted mb-2" scale="2" />
                        <p class="text-muted mb-0">{{ $t('components.order.configuration.location.search.startTyping') }}</p>
                    </div>
                </div>

                <div class="d-flex justify-content-end mt-4">
                    <div class="btn-list">
                        <b-button variant="light" size="sm" @click="closeLocationSearch">
                            {{ $t('components.order.configuration.location.search.cancel') }}
                        </b-button>
                        <b-button variant="primary" size="sm" @click="selectLocation" :disabled="!locationSearchModal.selectedLocation">
                            {{ $t('components.order.configuration.location.search.select') }}
                        </b-button>
                    </div>
                </div>
            </b-modal>

            <!-- Delivery Address Selection Modal -->
            <b-modal :id="deliveryAddressModal.id" v-model="deliveryAddressModal.show" size="lg" centered scrollable no-fade header-bg-variant="info" header-text-variant="white" title-tag="h4"
                     title-class="align-self-center" hide-footer>
                <template #modal-header-close><b-icon icon="close" scale="0.6" /></template>
                <template #modal-title>{{ $t('components.order.configuration.addresses.delivery.selection.title') }}</template>

                <div class="modal-search-content">
                    <!-- Original Detected Request -->
                    <div v-if="extractedLocationInfo" class="alert alert-info mb-3">
                        <p class="mb-0">
                            <b-icon icon="b-info-circle" class="mr-1"></b-icon>
                            <strong>Ursprünglich erkannte Anfrage:</strong>
                            <template v-if="extractedLocationInfo.locationIds && extractedLocationInfo.locationIds.length > 0">
                                <br />Id's: {{ extractedLocationInfo.locationIds.join(', ') }}
                            </template>
                            <template v-if="extractedLocationInfo.locationQuery">
                                <br />Standort: "{{ extractedLocationInfo.locationQuery }}"
                            </template>
                        </p>
                    </div>

                    <div v-if="deliveryAddressModal.isLoading" class="text-center py-4">
                        <loader type="dots" inline />
                        <p class="text-muted mt-2 mb-0">{{ $t('components.order.configuration.addresses.delivery.selection.loading') }}</p>
                    </div>

                    <div v-else-if="deliveryAddressModal.addresses.length === 0" class="text-center py-4">
                        <b-icon icon="geo-alt" class="text-muted mb-2" scale="2" />
                        <p class="text-muted mb-0">{{ $t('components.order.configuration.addresses.delivery.selection.noAddresses') }}</p>
                    </div>

                    <div v-else class="search-results">
                        <b-list-group flush>
                            <b-list-group-item v-for="address in deliveryAddressModal.addresses" :key="address.id"
                                               :class="{ 'selected': deliveryAddressModal.selectedAddress && deliveryAddressModal.selectedAddress.id === address.value.id }"
                                               @click="selectAddressFromList(address)" button>
                                <div class="d-flex justify-content-between align-items-start">
                                    <div class="flex-grow-1">
                                        <h6 class="mb-1 font-weight-semibold text-start">{{ configuration.location.name }}</h6>
                                        <div class="text-muted small text-start">
                                            <div v-if="address.value.street" class="d-flex align-items-center mb-1">
                                                {{ address.value.street }}
                                            </div>
                                            <div v-if="address.value.zip || address.value.city" class="d-flex align-items-center mb-1">
                                                {{ address.value.zip }} {{ address.value.city }}
                                            </div>
                                            <div v-if="address.value.canton || address.value.country" class="d-flex align-items-center">
                                                {{ address.value.canton }} {{ address.value.country }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="selection-indicator">
                                        <b-icon v-if="deliveryAddressModal.selectedAddress && deliveryAddressModal.selectedAddress.id === address.value.id" icon="check-circle-fill" variant="success"
                                                scale="1.2" />
                                    </div>
                                </div>
                            </b-list-group-item>
                        </b-list-group>
                    </div>
                </div>

                <div class="d-flex justify-content-end mt-4">
                    <div class="btn-list">
                        <b-button variant="light" size="sm" @click="closeDeliveryAddressSelection">
                            {{ $t('components.order.configuration.addresses.delivery.selection.cancel') }}
                        </b-button>
                        <b-button variant="primary" size="sm" @click="selectDeliveryAddress" :disabled="!deliveryAddressModal.selectedAddress">
                            {{ $t('components.order.configuration.addresses.delivery.selection.select') }}
                        </b-button>
                    </div>
                </div>
            </b-modal>
        </template>
    </div>
</template>

<script>
import { controlMapper, valueFormatter } from '@/assets/js/helper/entity'

import ControlInput from '@/components/form/ControlInput'
import Loader from '@/components/Loader'
import AddressLine from '@/components/order/AddressLine'

export default {
    name: 'OrderConfiguration',
    components: {
        AddressLine,
        Loader,
        ControlInput
    },
    props: {
        configuration: {
            type: Object,
            required: true
        },
        order: {
            type: Object,
            required: false
        },
        orderData: {
            type: Object,
            required: false
        }
    },
    data() {
        return {
            locationSearchModal: {
                id: 'locationSearchModal',
                show: false,
                searchQuery: '',
                results: [],
                selectedLocation: null,
                isLoading: false,
                searchTimeout: null
            },
            deliveryAddressModal: {
                id: 'deliveryAddressModal',
                show: false,
                addresses: [],
                selectedAddress: null,
                isLoading: false
            },
            editableOrderStates: ['Open', 'Executed', 'Ordered', 'Processing']
        }
    },
    computed: {
        isEditable() {
            return this.order && this.editableOrderStates.includes(this.order.orderState)
        },
        userIs() {
            return this.$store.getters['users/is']
        },
        userDefinition() {
            return this.$store.getters['users/definition']
        },
        user() {
            return controlMapper(this.userDefinition, this.$i18n.locale, [], this.configuration?.user)
        },
        locationIs() {
            return this.$store.getters['locations/is']
        },
        locationDefinition() {
            return this.$store.getters['locations/definition']
        },
        location() {
            return controlMapper(this.locationDefinition, this.$i18n.locale, [], this.configuration?.location)
        },
        locationAddressesIs() {
            return this.$store.getters['locationaddresses/is']
        },
        locationAddressesDefinition() {
            return this.$store.getters['locationaddresses/definition']
        },
        extractedLocationInfo() {
            if (!this.orderData || !this.orderData.extractorResult || !this.orderData.extractorResult.location) {
                return null
            }

            const location = this.orderData.extractorResult.location

            if (!location.locationQuery && (!location.locationIds || location.locationIds.length === 0)) {
                return null
            }

            return {
                locationQuery: location.locationQuery || '',
                locationIds: location.locationIds || []
            }
        }
    },
    methods: {
        valueFormatter,
        openLocationSearch() {
            this.locationSearchModal.show = true
            this.locationSearchModal.searchQuery = ''
            this.locationSearchModal.results = []
            this.locationSearchModal.selectedLocation = null
        },
        closeLocationSearch() {
            this.locationSearchModal.show = false
            this.locationSearchModal.searchQuery = ''
            this.locationSearchModal.results = []
            this.locationSearchModal.selectedLocation = null
        },
        searchLocations() {
            // Clear previous timeout
            if (this.locationSearchModal.searchTimeout) {
                clearTimeout(this.locationSearchModal.searchTimeout)
            }

            // Set loading state
            this.locationSearchModal.isLoading = true
            this.locationSearchModal.results = []

            // Debounce search
            this.locationSearchModal.searchTimeout = setTimeout(() => {
                if (this.locationSearchModal.searchQuery.trim().length < 2) {
                    this.locationSearchModal.isLoading = false
                    return
                }

                // Search locations using the store
                this.$store.dispatch('locations/searchByQueryLimited', {
                    data: {
                        query: this.locationSearchModal.searchQuery
                    }
                }).then(results => {
                    this.locationSearchModal.results = results || []
                    this.locationSearchModal.isLoading = false
                }).catch(error => {
                    console.error('Error searching locations:', error)
                    this.locationSearchModal.isLoading = false
                })
            }, 300)
        },
        selectLocationFromList(location) {
            this.locationSearchModal.selectedLocation = location
        },
        selectLocation() {
            if (this.locationSearchModal.selectedLocation) {
                // Emit event to parent component
                this.$emit('location-selected', this.locationSearchModal.selectedLocation)
                this.closeLocationSearch()
            }
        },
        openDeliveryAddressSelection() {
            if (!this.configuration.location) {
                return
            }

            this.deliveryAddressModal.show = true
            this.deliveryAddressModal.addresses = []
            this.deliveryAddressModal.selectedAddress = null
            this.deliveryAddressModal.isLoading = true

            // Load addresses for the current location
            this.$store.dispatch('locationaddresses/getAll', {
                by: {
                    name: 'location',
                    id: this.configuration.location.id
                }
            }).then(addresses => {
                this.deliveryAddressModal.addresses = addresses || []
                this.deliveryAddressModal.isLoading = false
            }).catch(error => {
                console.error('Error loading location addresses:', error)
                this.deliveryAddressModal.addresses = []
                this.deliveryAddressModal.isLoading = false
            })
        },
        closeDeliveryAddressSelection() {
            this.deliveryAddressModal.show = false
            this.deliveryAddressModal.addresses = []
            this.deliveryAddressModal.selectedAddress = null
        },
        selectAddressFromList(address) {
            this.deliveryAddressModal.selectedAddress = address.value
        },
        selectDeliveryAddress() {
            if (this.deliveryAddressModal.selectedAddress) {
                // Emit event to parent component
                this.$emit('delivery-address-selected', this.deliveryAddressModal.selectedAddress)
                this.closeDeliveryAddressSelection()
            }
        }
    },
    created() {
        this.$store.dispatch('users/getDefinition')
        this.$store.dispatch('locations/getDefinition')
        this.$store.dispatch('locationaddresses/getDefinition')
    }
}
</script>

<style lang="scss">
$orderconfiguration-title-padding-y: $spacer * 0.65 !default;
$orderconfiguration-title-padding-x: $spacer * 1 !default;
$orderconfiguration-title-bg: $gray-200 !default;
$orderconfiguration-title-font-size: inherit !default;
$orderconfiguration-title-color: inherit !default;

$orderconfiguration-list-gap: $spacer * 3 !default;
$orderconfiguration-list-padding-y: $spacer * 0.5 !default;
$orderconfiguration-list-padding-x: $orderconfiguration-title-padding-x !default;
$orderconfiguration-list-border: $border-width solid $border-color !default;
$orderconfiguration-list-title-font-size: $font-size-sm !default;
$orderconfiguration-list-title-font-weight: inherit !default;
$orderconfiguration-list-title-color: $dark !default;
$orderconfiguration-list-data-font-size: inherit !default;
$orderconfiguration-list-data-font-weight: inherit !default;
$orderconfiguration-list-data-color: inherit !default;
$orderconfiguration-list-data-content-gap: $spacer * 0.3 !default;

.order-configuration {

    .summary-title,
    .configuration-title {
        margin: 0;
        padding: $orderconfiguration-title-padding-y $orderconfiguration-title-padding-x;
        background-color: $orderconfiguration-title-bg;
        font-size: $orderconfiguration-title-font-size;
        color: $orderconfiguration-title-color;

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            margin: 0;
            font-size: inherit;
        }
    }

    .summary-list {
        margin-bottom: $orderconfiguration-list-gap;
        border-bottom: $orderconfiguration-list-border;

        > div {
            padding: 0 $orderconfiguration-list-padding-x;

            &.summary-separator {
                padding: $orderconfiguration-list-padding-y 0;

                &:first-child {
                    padding-top: 0;
                }

                &:last-child {
                    padding-top: 0;
                }

                hr {
                    margin: 0;
                    border-color: nth($orderconfiguration-list-border, 3);
                }
            }

            &.summary-price {
                &.is-total {
                    margin-top: $orderconfiguration-list-padding-y;
                    padding: $orderconfiguration-list-padding-y $orderconfiguration-list-padding-x;
                    border-top-color: nth($orderconfiguration-list-border, 3);
                }
            }
        }
    }

    .configuration-list {
        @include make-row(0);
        margin-bottom: $orderconfiguration-list-gap;
        border-top: $orderconfiguration-list-border;

        dt,
        dd {
            @include make-col-ready(0);
            margin: 0;
            padding: $orderconfiguration-list-padding-y $orderconfiguration-list-padding-x;
            border-bottom: $orderconfiguration-list-border;
        }

        dt {
            @include make-col(3);
            font-size: $orderconfiguration-list-title-font-size;
            font-weight: $orderconfiguration-list-title-font-weight;
            color: $orderconfiguration-list-title-color;
        }

        dd {
            @include make-col(9);
            font-size: $orderconfiguration-list-data-font-size;
            font-weight: $orderconfiguration-list-data-font-weight;
            color: $orderconfiguration-list-data-color;

            > * {
                margin-bottom: $orderconfiguration-list-data-content-gap;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }
    }

    .btn-list {
        display: flex;
        gap: 0.5rem;
    }
}

.modal-search-content {
    .search-control {
        .control-input {
            width: 100%;
        }
    }

    .search-results {
        max-height: 400px;
        overflow-y: auto;

        .list-group-item {
            cursor: pointer;
            transition: all 0.2s ease;
            border-left: 3px solid transparent;
            padding: 1rem;
            text-align: left;

            &:hover {
                background-color: $gray-100;
                border-left-color: $primary;
            }

            &.selected {
                background-color: rgba($primary, 0.1);
                border-color: $primary;
                border-left-color: $primary;
                color: $dark;
                box-shadow: 0 2px 4px rgba($primary, 0.15);

                .text-muted {
                    color: rgba($dark, 0.7) !important;
                }

                .selection-indicator {
                    .b-icon {
                        color: $primary !important;
                    }
                }
            }

            h6 {
                margin-bottom: 0.5rem;
                font-weight: 600;
                text-align: left;
            }

            .text-muted {
                font-size: 0.875rem;
                line-height: 1.4;
                text-align: left;
            }

            .selection-indicator {
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 24px;
                flex-shrink: 0;
            }
        }
    }

    .text-center {
        .b-icon {
            opacity: 0.6;
        }
    }
}
</style>
